import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Clock, MapPin } from 'lucide-react';
import { usePost } from '../hooks/usePosts';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { richTextHtml, isRichTextBlank } from '../lib/richText';
import { isUpcomingEvent, Post, POST_KIND_LABELS, postDateLabel } from '../types/post';
import NotFound from './NotFound';

/** Pagina di un evento, una novità o un articolo pubblicato (/novita/<slug>). */
export default function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading, notFound } = usePost(slug);

  useDocumentMeta(post?.title, post?.summary || undefined);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
      </div>
    );
  }
  // Anche una bozza aperta dal link diretto: per i visitatori non esiste.
  if (notFound || !post || !post.published) return <NotFound />;

  const isEvent = post.kind === 'evento';
  const facts = eventFacts(post);

  return (
    <article>
      <header className="relative overflow-hidden bg-[#2F1500]">
        {post.image_url && <img src={post.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2F1500]/90 via-[#2F1500]/60 to-[#2F1500]/30" />
        <div className="container relative z-10 py-16 md:py-24">
          <Link to="/novita" className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Tutti gli eventi e le novità
          </Link>
          <p className="mb-3 text-sm font-bold uppercase tracking-widest text-[#FFB95F]">{POST_KIND_LABELS[post.kind]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight text-white text-balance md:text-5xl">{post.title}</h1>
          {post.summary && <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg">{post.summary}</p>}
          {isEvent ? (
            <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {facts.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-2 text-white">
                  <Icon className="h-5 w-5 flex-none text-[#FFB95F]" />
                  <dt className="sr-only">{label}</dt>
                  <dd className="text-sm font-semibold">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-6 text-sm text-white/70">{postDateLabel(post)}</p>
          )}
        </div>
      </header>

      <div className="container max-w-3xl py-14">
        {isEvent && !isUpcomingEvent(post) && (
          <p className="mb-8 rounded-lg bg-muted px-4 py-3 text-sm font-semibold text-muted-foreground">Questo evento si è già svolto.</p>
        )}
        {!isRichTextBlank(post.body) && (
          <div
            className="rich-text text-base leading-relaxed text-foreground/80 md:text-lg"
            dangerouslySetInnerHTML={{ __html: richTextHtml(post.body) }}
          />
        )}
      </div>
    </article>
  );
}

function eventFacts(post: Post) {
  return [
    { icon: CalendarDays, label: 'Data', value: postDateLabel(post) },
    post.event_time && { icon: Clock, label: 'Orario', value: post.event_time },
    post.location && { icon: MapPin, label: 'Luogo', value: post.location },
  ].filter(Boolean) as { icon: typeof MapPin; label: string; value: string }[];
}
